import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import merge from "lodash.merge";
import { FilePlus2, Loader2 } from "lucide-react";
import { Suspense } from "react";
import {
  cardDefaults,
  cardRequestValidator,
  refreshIntervalOptions,
  type refreshIntervals,
  requestClientValidator,
} from "shared";
import { toast } from "sonner";
import type z from "zod";
import {
  CardTypeField,
  cardTypeFieldMapping,
} from "@/components/fields/card-type-field";
import type { FileData } from "@/components/fields/file-upload-field";
import { LimitFlexibilityInputField } from "@/components/fields/limit-flexibility-input";
import { RequestInfoFields } from "@/components/fields/request-info-fields";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import { Button } from "@/components/ui/button";
import { FormStepSeparator } from "@/components/ui/form-step-seperator";
import {
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAppForm } from "@/hooks/useCreateForm";
import { queryClient, trpc } from "@/lib/trpcClient";

const formValidator = cardRequestValidator.and(requestClientValidator);

export const Route = createFileRoute("/_authed/_org/requests/create/card")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();

  const { mutate: create, isPending } = useMutation(
    trpc.request.create.mutationOptions({
      onError: (error) => {
        toast.error("Error", {
          description: error.message,
          position: "bottom-left",
        });
      },
      onSuccess: (data) => {
        form.reset();

        queryClient.invalidateQueries(trpc.request.list.queryOptions());
        toast.success("Your request was submitted", {
          position: "bottom-left",
        });

        navigate({ params: { id: data.id }, to: "/requests/$id" });
      },
    }),
  );

  const {
    data: { user },
  } = useSuspenseQuery(trpc.auth.session.queryOptions());

  const form = useAppForm({
    defaultValues: merge(cardDefaults, {
      files: [] as FileData[],
      forUserId: user?.id,
      reason: "",
    }) as z.infer<typeof formValidator>,
    onSubmit: ({ value }) => {
      const { reason, files, ...data } = value;
      const { type, restrictions, ...rest } = data;
      const { refreshInterval, ...restrictionsRest } = restrictions;

      create({
        fileIds: files?.map((f) => f.id),
        reason,
        request: {
          data: {
            ...rest,
            restrictions: {
              ...restrictionsRest,
              refreshInterval:
                refreshInterval as (typeof refreshIntervals)[number],
            },
            type: type as "virtual" | "physical",
          },
          type: "card",
        },
      });
    },
    validators: {
      onSubmit: formValidator,
    },
  });

  return (
    <>
      <SheetHeader className="mb-2">
        <SheetTitle>Request a new card</SheetTitle>
        <SheetDescription>
          You can request a new card by filling out the form below. If an admin
          in your organization approves it, it will be issued for you.
        </SheetDescription>
      </SheetHeader>

      <form
        className="flex min-h-0 grow flex-col"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <Suspense
          fallback={
            <div className="px-6">
              <SheetSkeleton />
            </div>
          }
        >
          <div className="flex grow flex-col gap-4 overflow-y-auto border-neutral-300 border-y p-6 pt-6">
            <FormStepSeparator description="Details" />

            <CardTypeField
              form={form}
              fields={cardTypeFieldMapping}
              disallowIfPendingPhysicalRequests
            />

            <form.Subscribe
              selector={(state) => state.values.type}
              children={(type) =>
                type === "physical" && (
                  <div className="my-4">
                    <FormStepSeparator description="Shipping" />

                    <form.AppField
                      children={(field) => (
                        <field.AddressField
                          handleAddressSelect={(address) =>
                            form.setFieldValue("address", address)
                          }
                          label="Shipping Address"
                          required={true}
                        />
                      )}
                      name="addressId"
                    />
                  </div>
                )
              }
            />

            <form.AppField
              children={(field) => (
                <field.TextField label="Card Nickname" required={true} />
              )}
              name="name"
            />
            <FormStepSeparator description="Control Spend" />
            <LimitFlexibilityInputField
              hideLeniencyInput
              form={form}
              fields={{
                restrictions: "restrictions" as never,
              }}
            />
            <form.AppField
              children={(field) => (
                <field.SelectField
                  label="Frequency"
                  options={refreshIntervalOptions}
                  required={true}
                />
              )}
              name="restrictions.refreshInterval"
            />

            <RequestInfoFields
              form={form}
              fields={{
                files: "files",
                reason: "reason",
              }}
            />
          </div>

          <SheetFooter className="px-6">
            <Button type="submit" className="self-start">
              {isPending ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <FilePlus2 className="mr-2 size-4" />
              )}{" "}
              Request
            </Button>
          </SheetFooter>
        </Suspense>
      </form>
    </>
  );
}
